import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, ShieldOff, Ban } from "lucide-react";
import { Button } from "./ui/button";
import { tx } from "../lib/tx";

/**
 * ModerationWarningModal - Displays warning when user has 10-19 reports
 */
export function ModerationWarningModal({ warning, onClose }) {
  if (!warning?.show_warning) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-b from-orange-950 to-background border border-orange-500/50 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        >
          {/* Warning Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-orange-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-orange-500 mb-2">
            {tx("⚠️ Avertissement Important")}
          </h2>

          {/* Report Count */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-4">
            <div className="text-center">
              <span className="text-4xl font-bold text-orange-500">{warning.report_count}</span>
              <span className="text-lg text-orange-300">/20</span>
              <p className="text-sm text-orange-200 mt-1">{tx("signalements reçus")}</p>
            </div>
          </div>

          {/* Warning Message */}
          <p className="text-center text-white mb-4">
            {warning.message}
          </p>

          {/* Reports Until Ban */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-6">
            <p className="text-center text-red-300 text-sm">
              <strong>{tx("Il vous reste")} {warning.reports_until_ban} {tx("signalement(s)")}</strong> {tx("avant le bannissement définitif de votre compte.")}
            </p>
          </div>

          {/* Guidelines */}
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold text-white mb-2">{tx("📋 Rappel des règles :")}</h3>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>{tx("• Soyez respectueux envers les autres membres")}</li>
              <li>{tx("• Pas de contenu inapproprié ou offensant")}</li>
              <li>{tx("• Pas de harcèlement ni de menaces")}</li>
              <li>{tx("• Utilisez des photos authentiques de vous")}</li>
            </ul>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
          >
            {tx("J'ai compris, je m'engage à respecter les règles")}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * BannedModal - Displays when user is banned (403 error with BANNED prefix)
 */
export function BannedModal({ message, onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-b from-red-950 to-background border border-red-500/50 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        >
          {/* Ban Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
              <Ban className="w-12 h-12 text-red-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-red-500 mb-4">
            {tx("🚫 Compte Suspendu")}
          </h2>

          {/* Message */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <p className="text-center text-red-200">
              {message || tx("Votre compte a été suspendu définitivement suite à de nombreux signalements de la communauté. Cette décision est irrévocable.")}
            </p>
          </div>

          {/* Info */}
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <p className="text-sm text-muted-foreground text-center">
              {tx("Si vous pensez qu'il s'agit d'une erreur, vous pouvez nous contacter à l'adresse")} <span className="text-primary">support@loveconnect.fr</span>
            </p>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full rounded-xl border-red-500/50 text-red-300 hover:bg-red-500/10"
          >
            {tx("Fermer")}
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ModerationWarningModal;
